import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Factory, Landmark, Building2, ArrowRight, CheckCircle2, Settings,
  BarChart3, Monitor, Brain, Database, FileCheck, Download, FileText
} from 'lucide-react';

const industries = [
  {
    icon: Factory,
    title: 'Private Industry',
    description: 'Mid-market and enterprise organizations seeking better execution, lower costs, cleaner reporting, and modern technology.',
    needs: [
      'Cost reduction tied to performance targets',
      'Process improvement and cycle-time reduction',
      'KPI frameworks and management dashboards',
      'IT strategy and project delivery discipline',
      'AI/GenAI Adoption with clear controls & Creation of Digital Assets',
    ],
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    icon: Landmark,
    title: 'Public Sector',
    description: 'Agencies, departments, and authorities seeking measurable performance improvement and stronger controls.',
    needs: [
      'Performance measurement frameworks',
      'Operational efficiency and process redesign',
      'Budget modernization and forecasting',
      'PMO governance and project recovery',
      'Data validation and reporting reliability',
      'AI/GenAI Adoption & Ideation for Digital Assets',
    ],
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    icon: Building2,
    title: 'Quasi-Government & Regulated',
    description: 'Utilities, authorities, higher education, transit, and healthcare systems needing disciplined operations.',
    needs: [
      'Operating model clarity and governance',
      'Risk and control frameworks',
      'Data migration and analytics readiness',
      'ERP selection and implementation',
      'Process improvement across delivery',
      'AI/GenAI Adoption & Ideation for Digital Assets',
    ],
    gradient: 'from-purple-500 to-violet-500',
  },
];

const commonEngagements = [
  { icon: Settings, label: 'Operational efficiency and process redesign' },
  { icon: BarChart3, label: 'KPI dashboards and performance reporting' },
  { icon: Monitor, label: 'ERP selection and implementation' },
  { icon: Database, label: 'Data cleanup and migration planning' },
  { icon: Brain, label: 'AI/GenAI/LLM & Digital Assets governance and policies' },
  { icon: FileCheck, label: 'PMO and program delivery support' },
];

const IndustriesPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="industries-page">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden" data-testid="industries-hero">
        <div className="absolute inset-0 bg-radial-top" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              Industries We <span className="text-gradient">Serve</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Supporting organizations under real constraints—private and public oversight, tight budgets, and high service expectations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
              data-testid={`industry-${index}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${industry.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                  <industry.icon className="text-white" size={40} />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-4">{industry.title}</h2>
                <p className="text-lg text-muted-foreground">{industry.description}</p>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="p-8 rounded-2xl glass glow-border">
                  <h3 className="text-lg font-semibold text-foreground mb-6">Typical Needs</h3>
                  <ul className="space-y-4">
                    {industry.needs.map((need, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <CheckCircle2 className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                        <span className="text-muted-foreground">{need}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Common Engagements */}
      <section className="py-20 relative bg-grid-fine" data-testid="common-engagements">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Common <span className="text-gradient">Engagement Types</span></h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commonEngagements.map((e, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center space-x-4 p-5 rounded-xl glass"
                data-testid={`engagement-type-${i}`}
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                  <e.icon className="text-cyan-400" size={22} />
                </div>
                <span className="text-foreground font-medium text-sm">{e.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capability Statement */}
      <section className="py-24" data-testid="capability-statement-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-4xl font-bold mb-6">
                Capability <span className="text-gradient">Statement</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Download our comprehensive Capability Statement to learn more about ANJO Consultants&apos; qualifications, experience, and service offerings.
              </p>
              <ul className="space-y-3 mb-8">
                {['Company overview and core competencies', 'Past performance and relevant experience', 'Key differentiators and approach', 'NAICS codes and certifications', 'Contact information for procurement'].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="text-cyan-400 flex-shrink-0 mt-1" size={16} />
                    <span className="text-muted-foreground text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="/ANJO-Capability-Statement.pdf" download="ANJO-Consultants-Capability-Statement.pdf" target="_blank" rel="noopener noreferrer" data-testid="download-capability-btn" className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-all shadow-lg shadow-cyan-500/25">
                  <Download className="mr-2" size={20} />
                  Download Statement
                </a>
                <Link to="/contact" data-testid="request-soq-btn" className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-medium border border-border hover:border-cyan-500/50 hover:bg-cyan-500/5 text-foreground transition-all">
                  Request Custom SOQ
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="p-8 rounded-2xl glass glow-border">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                    <FileText className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">ANJO Consultants</h3>
                    <p className="text-muted-foreground text-sm">Capability Statement</p>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="h-3 bg-secondary rounded w-full" />
                  <div className="h-3 bg-secondary rounded w-5/6" />
                  <div className="h-3 bg-secondary rounded w-4/6" />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-cyan-500/10 p-4 rounded-lg border border-cyan-500/20">
                    <p className="text-xs text-muted-foreground mb-1">Services</p>
                    <p className="text-sm font-semibold text-foreground">9 Core Areas</p>
                  </div>
                  <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                    <p className="text-xs text-muted-foreground mb-1">Industries</p>
                    <p className="text-sm font-semibold text-foreground">3 Sectors</p>
                  </div>
                </div>
                <div className="text-center text-muted-foreground text-xs">PDF Document • SOQ-Ready Format</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 animate-gradient" />
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-foreground mb-6">
            Serving your <span className="text-gradient">industry?</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Let&apos;s discuss how ANJO Consultants can help your specific organization.
          </p>
          <Link to="/contact" data-testid="industries-cta" className="group inline-flex items-center px-10 py-5 rounded-xl font-semibold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-all shadow-lg shadow-cyan-500/25">
            Get in Touch
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default IndustriesPage;
